import { useState } from 'react'
import { Sparkles, Copy, Check } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { AssistantSegment } from '../types'
import ToolCallCard from './ToolCallCard'
import ThinkingIndicator from './ThinkingIndicator'

const FRIENDLY_LABELS: Record<string, string> = {
    query_keywords: 'Searching keywords',
    get_keyword_by_id: 'Fetching keyword',
    list_documents: 'Searching documents',
    get_document_by_id: 'Fetching document',
    aggregate_keywords: 'Analyzing keywords',
    aggregate_documents: 'Analyzing documents',
    count_keywords_by_status: 'Counting keywords',
    count_documents: 'Counting documents',
    get_pipeline_config: 'Reading pipeline',
    create_keyword: 'Creating keyword',
    update_keyword: 'Updating keyword',
    create_document: 'Creating document',
    update_document: 'Updating document',
}

const markdownComponents = {
    p: ({ children }: any) => <p className='my-2 first:mt-0 last:mb-0'>{children}</p>,
    ul: ({ children }: any) => <ul className='my-2 list-disc pl-5 space-y-0.5'>{children}</ul>,
    ol: ({ children }: any) => <ol className='my-2 list-decimal pl-5 space-y-0.5'>{children}</ol>,
    li: ({ children }: any) => <li className='my-0.5'>{children}</li>,
    h1: ({ children }: any) => <h1 className='mt-4 mb-2 text-base font-semibold'>{children}</h1>,
    h2: ({ children }: any) => <h2 className='mt-3 mb-2 text-base font-semibold'>{children}</h2>,
    h3: ({ children }: any) => <h3 className='mt-3 mb-1.5 text-sm font-semibold'>{children}</h3>,
    pre: ({ children }: any) => (
        <pre className='my-2 overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100'>{children}</pre>
    ),
    code: ({ children, className }: any) =>
        className ? (
            <code className='text-xs'>{children}</code>
        ) : (
            <code className='rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-800'>{children}</code>
        ),
    table: ({ children }: any) => (
        <div className='my-2 overflow-x-auto rounded-lg ring-1 ring-slate-200'>
            <table className='min-w-full text-xs'>{children}</table>
        </div>
    ),
    th: ({ children }: any) => (
        <th className='border-b border-slate-200 bg-slate-50 px-2.5 py-1.5 text-left font-semibold text-slate-700'>
            {children}
        </th>
    ),
    td: ({ children }: any) => <td className='border-b border-slate-100 px-2.5 py-1.5'>{children}</td>,
}

type Props = {
    segments: AssistantSegment[]
    isStreaming?: boolean
    isAwaitingFirstEvent?: boolean
    showCopy?: boolean
}

export default function AssistantTurn({ segments, isStreaming, isAwaitingFirstEvent, showCopy = true }: Props) {
    const [copied, setCopied] = useState(false)

    const handleCopy = () => {
        const fullText = segments
            .filter(s => s.kind === 'text')
            .map(s => (s as any).text)
            .join('\n\n')
        navigator.clipboard.writeText(fullText).catch(() => {})
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }

    const lastSegment = segments[segments.length - 1]
    const showThinking =
        isStreaming &&
        (segments.length === 0 ||
            (lastSegment?.kind === 'tool' && lastSegment.status === 'running') ||
            (isAwaitingFirstEvent && segments.length === 0))

    let thinkingLabel = 'Thinking'
    if (lastSegment?.kind === 'tool' && lastSegment.status === 'running') {
        thinkingLabel = FRIENDLY_LABELS[lastSegment.name] || 'Working'
    }

    const hasAnyText = segments.some(s => s.kind === 'text' && s.text.trim().length > 0)

    return (
        <div className='group flex gap-3'>
            <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-main/10'>
                <Sparkles className='h-3.5 w-3.5 text-primary-main' />
            </div>

            <div className='min-w-0 flex-1 space-y-3 pt-0.5'>
                {segments.map((segment, idx) => {
                    if (segment.kind === 'text') {
                        if (!segment.text) return null
                        return (
                            <div
                                key={`text-${idx}`}
                                className='prose prose-sm prose-slate max-w-none text-[14.5px] leading-relaxed text-slate-800'
                            >
                                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                                    {segment.text}
                                </ReactMarkdown>
                            </div>
                        )
                    }
                    return (
                        <ToolCallCard
                            key={`tool-${segment.id}`}
                            name={segment.name}
                            status={segment.status}
                            success={segment.success}
                            durationMs={segment.durationMs}
                            args={segment.args}
                            result={segment.result}
                        />
                    )
                })}

                {showThinking && (
                    <div>
                        <ThinkingIndicator label={thinkingLabel} />
                    </div>
                )}

                {showCopy && !isStreaming && hasAnyText && (
                    <div className='flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100'>
                        <button
                            type='button'
                            onClick={handleCopy}
                            className='flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                            title='Copy message'
                        >
                            {copied ? (
                                <>
                                    <Check className='h-3 w-3' />
                                    Copied
                                </>
                            ) : (
                                <>
                                    <Copy className='h-3 w-3' />
                                    Copy
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    )
}
